import { HttpErrorResponse } from '@angular/common/http';
import { formatNumberForLocale, wrapMixedLocaleBidi } from '../utils/mixed-locale-bidi';

export type RateLimitCategory = 'activation' | 'resend' | 'forgot' | 'login' | 'default';

const TIME = '{{time}}';
const DEFAULT_MSG = 'Unable to process the request. Please try again later.';

const RATE_LIMIT_RULES = [
  {
    category: 'activation',
    paths: ['resendActivationLink'],
    labelKey: 'rate_limit_activation_msg',
    fallback: 'Activation link resend limit reached for this user. Please try again in {{time}}.',
  },
  {
    category: 'login',
    paths: ['loginportal', 'loginIndividual', 'checkLogin'],
    labelKey: 'rate_limit_login_msg',
    fallback: 'Too many login attempts. Please try again in {{time}}.',
  },
  {
    category: 'forgot',
    paths: ['checkForgotCredentials', 'forgotCredentials', 'checkResetLinkExpiry', 'checkResetPassword', 'resetPassword'],
    labelKey: 'rate_limit_forgot_credentials_msg',
    fallback: 'A reset request was recently sent to this email address. Please try again in {{time}}.',
  },
  {
    category: 'resend',
    paths: [
      'regeneratePinCode',
      'resendHistoryLink',
      'resendByWhatsapp',
      'resendBySms',
      'resendByEmail',
      'resendDocument',
      'resendDocumentLink',
    ],
    labelKey: 'rate_limit_resend_msg',
    fallback: 'Maximum resend limit reached. Please try again in {{time}}.',
  },
] as const;

/** English when labels are not loaded yet. */
const EN = {
  rate_limit_hour: 'hour',
  rate_limit_hours: 'hours',
  rate_limit_minute: 'minute',
  rate_limit_minutes: 'minutes',
  rate_limit_second: 'second',
  rate_limit_seconds: 'seconds',
  rate_limit_time_joiner: ' ',
};

function pickLabel(labels: Record<string, string> | null | undefined, key: keyof typeof EN): string {
  const value = labels?.[key];
  if (value == null) {
    return EN[key];
  }
  return key === 'rate_limit_time_joiner' ? value : value.trim() || EN[key];
}

/** Format [hours, minutes, seconds]; skip zeros. Unit words from labels. */
export function formatDurationParts(hours: number, minutes: number, seconds: number, labels?: Record<string, string> | null, language?: string | null): string {
  const parts: string[] = [];
  for (const [count, kind] of [
    [hours, 'hour'],
    [minutes, 'minute'],
    [seconds, 'second'],
  ] as const) {
    if (count <= 0) {
      continue;
    }
    const key = `rate_limit_${kind}${count === 1 ? '' : 's'}` as keyof typeof EN;
    parts.push(`${formatNumberForLocale(count, language)} ${pickLabel(labels, key)}`);
  }
  return parts.length ? parts.join(pickLabel(labels, 'rate_limit_time_joiner')) : '';
}

export function extractRateLimitWaitTime(
  error: HttpErrorResponse,
  labels?: Record<string, string> | null,
  language?: string | null,
): string {
  const body = error.error as { errorDescription?: unknown; error_description?: unknown } | null;
  const raw = body?.errorDescription ?? body?.error_description;

  // Legacy: backend still returns a localized wait string in some envs.
  if (typeof raw === 'string') {
    return raw.trim();
  }

  if (!Array.isArray(raw) || raw.length !== 3) {
    return '';
  }
  const [hours, minutes, seconds] = raw.map(Number);
  if ([hours, minutes, seconds].some((n) => !Number.isFinite(n) || n < 0)) {
    return '';
  }
  return formatDurationParts(hours, minutes, seconds, labels, language);
}

export function resolveRateLimitCategory(requestUrl: string): RateLimitCategory {
  const path = requestUrl.split('?')[0] ?? requestUrl;
  return RATE_LIMIT_RULES.find((rule) => rule.paths.some((p) => path.includes(p)))?.category ?? 'default';
}

function applyWaitTime(template: string, waitTime: string, language?: string | null): string {
  if (waitTime) {
    const lang =
      language ??
      (typeof localStorage !== 'undefined' ? localStorage.getItem('language') : null);
    // Keep a normal "." but pin it with RLM so it stays after the Arabic duration.
    if (lang === 'ar' && template.includes(`${TIME}.`)) {
      return template.replaceAll(`${TIME}.`, `${waitTime}.\u200F`);
    }
    return template.replaceAll(TIME, waitTime);
  }
  if (!template.includes(TIME)) {
    return template;
  }
  const kept = template.split(/(?<=[.!?؟])\s+/u).filter((part) => !part.includes(TIME));
  return kept.length
    ? kept.join(' ').trim()
    : template.replace(TIME, '').replace(/\s+/g, ' ').trim();
}

export function buildRateLimitMessage(
  requestUrl: string,
  error: HttpErrorResponse,
  labels: Record<string, string> | null,
  language?: string | null,
): string {
  const waitTime = extractRateLimitWaitTime(error, labels, language);
  const path = requestUrl.split('?')[0] ?? requestUrl;
  const rule = RATE_LIMIT_RULES.find((r) => r.paths.some((p) => path.includes(p)));
  const template = rule
    ? labels?.[rule.labelKey] ?? rule.fallback
    : waitTime
      ? labels?.['rate_limit_msg_with_time'] ?? 'Unable to process the request. Please try again in {{time}}.'
      : labels?.['rate_limit_msg'] ?? DEFAULT_MSG;
  const message = applyWaitTime(template, waitTime, language);

  return wrapMixedLocaleBidi(message, language);
}
